import { useQuery } from "@tanstack/react-query";
import {
	PodWorkspaceService,
	type PodWorkspace,
} from "@/lib/podWorkspace/podWorkspace.service";
import { PodcastService, type Podcast } from "@/lib/podcast/podcast.service";
import { isPodcastPipelineProcessing } from "@/features/podWorkspace/podcastPipelineStatus.util";

interface UsePodWorkspacesOptions {
	/** Enable automatic fetching on mount. Default: true */
	enabled?: boolean;
}

interface UsePodWorkspacesReturn {
	workspaces: PodWorkspace[];
	podcastsByWorkspace: Record<string, Podcast | null>;
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
}

/**
 * Custom hook to fetch and manage pod workspaces with their associated podcasts.
 * Uses TanStack Query for automatic polling when podcasts are processing.
 */
export function usePodWorkspaces(
	options: UsePodWorkspacesOptions = {},
): UsePodWorkspacesReturn {
	const { enabled = true } = options;

	// Fetch workspaces and podcasts
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["podWorkspaces"],
		queryFn: async () => {
			// Fetch all workspaces
			const workspaces = await PodWorkspaceService.getAllWorkspacesForUser();

			// Fetch podcasts for each workspace in parallel
			const podcastMap: Record<string, Podcast | null> = {};
			await Promise.all(
				workspaces.map(async (ws) => {
					try {
						const podcasts = await PodcastService.getPodcastsByWorkspace(ws.id);
						// Prioritize: COMPLETED > PROCESSING > any other status
						const firstPodcast =
							podcasts.find((p) => p.status === "COMPLETED") ||
							podcasts.find((p) => p.status === "PROCESSING") ||
							podcasts[0] ||
							null;
						podcastMap[ws.id] = firstPodcast;
					} catch (err) {
						console.error(
							`Failed to fetch podcasts for workspace ${ws.id}:`,
							err,
						);
						podcastMap[ws.id] = null;
					}
				}),
			);

			return { workspaces, podcastsByWorkspace: podcastMap };
		},
		enabled,
		// Smart polling: only poll when there are processing podcasts
		refetchInterval: (query) => {
			const data = query.state.data;
			if (!data) return false;

			const hasProcessing = Object.values(data.podcastsByWorkspace).some((p) =>
				isPodcastPipelineProcessing(p),
			);

			return hasProcessing ? 5000 : false; // Poll every 5s when processing
		},
		refetchIntervalInBackground: false, // Pause when tab inactive
		staleTime: 0, // Always refetch on mount
	});

	return {
		workspaces: data?.workspaces || [],
		podcastsByWorkspace: data?.podcastsByWorkspace || {},
		isLoading,
		error: error as Error | null,
		refetch,
	};
}
